import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Inbox, Images, Wrench, FileText, Receipt, Settings as SettingsIcon, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/admin", label: "Dashboard", short: "Home", icon: LayoutDashboard, end: true },
  { to: "/admin/leads", label: "Leads", short: "Leads", icon: Inbox },
  { to: "/admin/invoices", label: "Invoices", short: "Invoices", icon: Receipt },
  { to: "/admin/gallery", label: "Gallery", short: "Gallery", icon: Images },
  { to: "/admin/services", label: "Services", short: "Services", icon: Wrench },
  { to: "/admin/content", label: "Content", short: "Content", icon: FileText },
  { to: "/admin/settings", label: "Settings", short: "More", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-100 lg:flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex lg:flex-col w-64 bg-slate-900 text-slate-300 p-5 sticky top-0 h-screen">
        <div className="mb-8 px-1">
          <div className="inline-flex bg-white rounded-xl p-2.5 mb-2">
            <img src="/logo.png" alt="Speedex International" className="h-8 w-auto" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-green-500 font-semibold px-0.5">Admin Panel</div>
        </div>
        <nav className="space-y-1 flex-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`sidebar-${n.label.toLowerCase()}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold transition-colors ${isActive ? "bg-green-600 text-white" : "hover:bg-slate-800 hover:text-white"}`}>
              <n.icon className="h-5 w-5" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 pt-4 mt-4">
          <div className="px-3 text-sm text-slate-400 mb-3">Signed in as <span className="text-white font-semibold">{user?.name || "Admin"}</span></div>
          <button onClick={doLogout} data-testid="sidebar-logout" className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-semibold w-full hover:bg-slate-800 hover:text-white transition-colors">
            <LogOut className="h-5 w-5" /> Log out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-slate-900 text-white flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2.5">
          <div className="inline-flex bg-white rounded-lg p-1.5">
            <img src="/logo.png" alt="Speedex International" className="h-6 w-auto" />
          </div>
          <span className="font-display font-bold text-sm">Admin Panel</span>
        </div>
        <button onClick={doLogout} data-testid="mobile-logout" className="p-2"><LogOut className="h-5 w-5" /></button>
      </div>

      {/* Content */}
      <main className="flex-1 pb-24 lg:pb-0 min-w-0">
        <div className="max-w-5xl mx-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 grid grid-cols-7">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`bottomnav-${n.label.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 py-2 text-[9px] font-semibold ${isActive ? "text-green-600" : "text-slate-400"}`}>
            <n.icon className="h-4.5 w-4.5" /> {n.short}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
